import React, { useMemo } from 'react';

import styles from './PatientTables.module.css';

import { sortByAttribute } from '../../services/sort';
import { removeMinutes } from '../../services/parseDates';
import { compareDates } from '../../services/General';

import PatientTable from '../../components/PatientTablesPage/PatientTable/PatientTable';
import PatientTableTimestamps from '../../components/PatientTablesPage/PatientTableTimestamps/PatientTableTimestamps';
import ErrorSpinnerWrapper from '../../components/UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';

import { PatientOutletContext } from '../../interfaces/Patient';
import { DataElementByHour } from '../../interfaces/PhysiologicalMonitoring';
import { Parameter, ProfileSheetSectionParameters } from '../../interfaces/Profile';

import { useOutletContext } from 'react-router-dom';
import useTablesPatient from '../../hooks/useTablesPatient';
import { canEditCaseView } from '../../services/user';

const normalizeId = (id: any) => (id != null ? String(id) : null);

const PatientTables: React.FC = () => {
    const { patient, caso, elements, sheetParameters, dispatchElements }: PatientOutletContext = useOutletContext();

    const patientPreconfigID =
        patient?.patient_preconfigs?.find(pp => pp.end_date == null)?.id
        ?? patient?.patient_preconfigs?.[0]?.id;

    const activeProfileID =
        caso.follow_ups?.find(fu => fu.end_date === null)?.profile?.id
        ?? caso.follow_ups?.[0]?.profile?.id
        ?? null;

    const {
        loading,
        error,
        profileSections,
        dataElementsLab,
        mergedInquiries,
        mergedSheetParams
    } = useTablesPatient(caso.id, patientPreconfigID, activeProfileID, sheetParameters);

    const valueIds = useMemo(() => {
        const set = new Set<string>();

        elements.forEach(e =>
            Object.keys(e.values).forEach(k => set.add(String(k)))
        );

        dataElementsLab.forEach(e =>
            Object.keys(e.values).forEach(k => set.add(String(k)))
        );

        return set;
    }, [elements, dataElementsLab]);

    const preconfigInquiryIds = useMemo(
        () =>
            new Set(
                mergedInquiries
                    .map(i => i.inquiry_id)
                    .filter((id): id is number => id != null)
                    .map(String)
            ),
        [mergedInquiries]
    );

    const fallbackCvParameters = useMemo<Parameter[]>(() => {
        return mergedSheetParams.map(param => ({
            id: String(param.sheet_parameter_id),
            name: String(param.sheet_parameter_id),
            visible: true,
            min_limit: param.min_limit ?? undefined,
            max_limit: param.max_limit ?? undefined,
        } as Parameter));
    }, [mergedSheetParams]);

    const fallbackInquiryParameters = useMemo<Parameter[]>(() => {
        return mergedInquiries.map(inquiry => ({
            id: String(inquiry.inquiry_id),
            name: inquiry.inquiry?.name ?? String(inquiry.inquiry_id),
            visible: true,
        } as Parameter));
    }, [mergedInquiries]);

    const rawCvSection = useMemo(
        () => profileSections.find(s => s.section.section_code === 'cv'),
        [profileSections]
    );

    const effectiveCvSection = useMemo<ProfileSheetSectionParameters | null>(() => {
        if (patientPreconfigID == null) {
            if (!rawCvSection) return null;

            const filteredParams = rawCvSection.parameters.filter(param => {
                const id = normalizeId(param.id);
                return !!id && valueIds.has(id);
            });

            if (filteredParams.length === 0) {
                return null;
            }

            return {
                ...rawCvSection,
                section: {
                    ...rawCvSection.section,
                    name: 'CV',
                },
                parameters: filteredParams,
            };
        }

        if (rawCvSection && rawCvSection.parameters.length > 0) {
            return {
                ...rawCvSection,
                section: {
                    ...rawCvSection.section,
                    name: 'CV',
                },
            };
        }

        if (fallbackCvParameters.length === 0) {
            return rawCvSection
                ? {
                    ...rawCvSection,
                    section: {
                        ...rawCvSection.section,
                        name: 'CV',
                    },
                }
                : null;
        }

        const syntheticCvSection: ProfileSheetSectionParameters = rawCvSection
            ? {
                ...rawCvSection,
                section: {
                    ...rawCvSection.section,
                    name: 'CV',
                },
                parameters: fallbackCvParameters
            }
            : {
                id: -999999,
                order: -999999,
                section: {
                    id: -999999,
                    name: 'CV',
                    section_code: 'cv',
                    name_mw_translations_id: null,
                    name_translations: [],
                },
                parameters: fallbackCvParameters
            } as unknown as ProfileSheetSectionParameters;

        return syntheticCvSection;
    }, [patientPreconfigID, rawCvSection, fallbackCvParameters, valueIds]);

    const rawNonCvSections = useMemo(
        () => profileSections.filter(section => section.section.section_code !== 'cv'),
        [profileSections]
    );

    const fallbackInquirySection = useMemo<ProfileSheetSectionParameters | null>(() => {
        if (patientPreconfigID == null || fallbackInquiryParameters.length === 0) {
            return null;
        }

        const sectionWithInquiries = rawNonCvSections.find(section =>
            section.parameters.some(param => {
                const id = normalizeId(param.id);
                return !!id && preconfigInquiryIds.has(id);
            })
        );

        if (sectionWithInquiries) {
            const existingIds = new Set(
                sectionWithInquiries.parameters
                    .map(param => normalizeId(param.id))
                    .filter((id): id is string => !!id)
            );

            const missingPreconfigInquiryParams = fallbackInquiryParameters.filter(param => {
                const id = normalizeId(param.id);
                return !!id && !existingIds.has(id);
            });

            return {
                ...sectionWithInquiries,
                parameters: [
                    ...sectionWithInquiries.parameters,
                    ...missingPreconfigInquiryParams
                ]
            };
        }

        const sectionByName = rawNonCvSections.find(section =>
            section.section.name?.toLowerCase().includes('encuesta')
        );

        if (sectionByName) {
            return {
                ...sectionByName,
                parameters: fallbackInquiryParameters
            };
        }

        const syntheticInquirySection: ProfileSheetSectionParameters = {
            id: -999998,
            order: -999998,
            section: {
                id: -999998,
                name: 'Encuestas',
                section_code: 'encuestas',
                name_mw_translations_id: null,
                name_translations: [],
            },
            parameters: fallbackInquiryParameters
        } as unknown as ProfileSheetSectionParameters;

        return syntheticInquirySection;
    }, [patientPreconfigID, fallbackInquiryParameters, rawNonCvSections, preconfigInquiryIds]);

    const filteredNonCvSections = useMemo(() => {
        const mappedSections = rawNonCvSections.map(section => ({
            ...section,
            parameters: section.parameters.filter(p => {
                const id = normalizeId(p.id);
                if (!id) return false;

                const hasValue = valueIds.has(id);
                const isInPreconfig =
                    patientPreconfigID != null && preconfigInquiryIds.has(id);

                return hasValue || isInPreconfig;
            })
        }));

        const nonEmptySections = mappedSections.filter(section => section.parameters.length > 0);

        const fallbackSection = fallbackInquirySection;
        if (!fallbackSection) {
            return nonEmptySections;
        }

        const alreadyIncluded = nonEmptySections.some(section => section.id === fallbackSection.id);

        return alreadyIncluded
            ? nonEmptySections.map(section =>
                section.id === fallbackSection.id ? fallbackSection : section
            )
            : [...nonEmptySections, fallbackSection];
    }, [rawNonCvSections, valueIds, patientPreconfigID, preconfigInquiryIds, fallbackInquirySection]);

    const filteredSections = useMemo(() => {
        return [
            ...(effectiveCvSection ? [effectiveCvSection] : []),
            ...filteredNonCvSections
        ];
    }, [effectiveCvSection, filteredNonCvSections]);

    const CVSection = effectiveCvSection;

    const dataWithLab = [...elements, ...dataElementsLab].sort((a, b) => compareDates(a.date, b.date));

    const tableElementsByHour: DataElementByHour[] = [];

    dataWithLab.forEach((el, i) => {
        const hour = removeMinutes(el.date);

        if (i === 0 || removeMinutes(tableElementsByHour.at(-1)!.date) !== hour) {
            tableElementsByHour.push({ date: hour, elements: [el] });
        } else {
            tableElementsByHour.at(-1)!.elements.push(el);
        }
    });

    return (
        <ErrorSpinnerWrapper loading={loading} error={error}>
            <div className={styles.container}>
                <table className={styles.tableGeneral}>
                    {CVSection && CVSection.parameters.length > 0 && (
                        <PatientTableTimestamps
                            parameters={CVSection.parameters}
                            data={tableElementsByHour}
                            readOnly={!canEditCaseView(caso)}
                            dispatchElements={dispatchElements}
                        />
                    )}
                    <tbody className={styles.tablesContainer}>
                        {sortByAttribute(filteredSections, 'order').map(section => (
                            <PatientTable
                                key={section.id}
                                section={section}
                                data={tableElementsByHour}
                                readOnly={!canEditCaseView(caso)}
                                dispatchElements={dispatchElements}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </ErrorSpinnerWrapper>
    );
};

export default PatientTables;